import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Mail, Phone, Clock, MapPin, Video } from "lucide-react";
import { useScheduleCall } from "../context/ScheduleCallContext";

const Contact = () => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  const { openScheduleCall } = useScheduleCall();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

      const response = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const contentType = response.headers.get("content-type");
      let result;

      if (contentType && contentType.includes("application/json")) {
        result = await response.json();
      } else {
        throw new Error(
          `Backend returned non-JSON response. Check if backend is running at ${API_URL}`
        );
      }

      if (response.ok && result.success) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", company: "", subject: "", message: "" });
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        throw new Error(result.error || "Failed to send email");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setSubmitStatus("error");
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail className="w-5 h-5" strokeWidth={1.75} />,
      title: "Email",
      info: "master@purposelabs.in",
      action: "mailto:master@purposelabs.in",
    },
    {
      icon: <Phone className="w-5 h-5" strokeWidth={1.75} />,
      title: "Phone / WhatsApp",
      info: "+91 8178462090",
      action: "tel:+918178462090",
    },
    {
      icon: <Clock className="w-5 h-5" strokeWidth={1.75} />,
      title: "Working Hours",
      info: "Mon–Sat 9AM–9PM IST",
      action: null,
    },
    {
      icon: <MapPin className="w-5 h-5" strokeWidth={1.75} />,
      title: "Location",
      info: "Meerut, India — Remote delivery available",
      action: null,
    },
  ];

  return (
    <section id="contact" className="section-padding bg-dark-400 relative overflow-hidden">
      <div className="absolute inset-0 bg-mesh-gradient opacity-40 pointer-events-none" />

      <div className="section-container relative z-10">
        <motion.div
          ref={ref}
          className="text-center mb-14"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className="text-accent-rose font-semibold text-sm uppercase tracking-widest">
            Let's Talk
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-5 gradient-text">
            Ready to Build Something That Makes Money?
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
            Tell us about your project. We'll respond within 24 hours with a
            free consultation and project estimate.
          </p>
          <motion.button
            onClick={openScheduleCall}
            className="btn-primary text-base px-6 py-3 inline-flex items-center gap-2"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <Video className="w-5 h-5" strokeWidth={1.75} aria-hidden />
            Book on Zoom — Free Consultation
          </motion.button>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
          {/* Form */}
          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <div className="glass-panel p-7 md:p-9">
              <h3 className="font-display text-xl font-bold text-white mb-6">
                Send Us Your Project Details
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-dark-200/80 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-dark-200/80 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-dark-200/80 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="Your Company LLC"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      What do you need? *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-dark-200/80 border border-white/10 rounded-xl text-white focus:outline-none focus:border-primary-500 transition-colors"
                    >
                      <option value="">Select a service</option>
                      <option value="schedule-call">Schedule a Call</option>
                      <option value="lead-gen-website">Lead Generation Website</option>
                      <option value="booking-platform">Booking / Reservation Platform</option>
                      <option value="ai-call-agent">AI Inbound Call Agent</option>
                      <option value="custom-crm">Custom CRM System</option>
                      <option value="web-application">Full-Stack Web Application</option>
                      <option value="automation">Business Automation</option>
                      <option value="other">Other / Not Sure</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Project Details *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-dark-200/80 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    placeholder="Describe your project, timeline, and budget range..."
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={!isSubmitting ? { scale: 1.01 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.99 } : {}}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <motion.div
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                      Sending...
                    </span>
                  ) : (
                    "Send Project Inquiry →"
                  )}
                </motion.button>

                {submitStatus === "success" && (
                  <motion.div
                    className="p-4 bg-accent-emerald/10 border border-accent-emerald/30 rounded-xl text-accent-emerald text-sm"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    Message sent! We'll get back to you within 24 hours.
                  </motion.div>
                )}

                {submitStatus === "error" && (
                  <motion.div
                    className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    Failed to send. Please email us directly at master@purposelabs.in
                  </motion.div>
                )}
              </form>
            </div>
          </motion.div>

          {/* Contact info */}
          <motion.div
            className="lg:col-span-2 space-y-5"
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
          >
            {contactInfo.map((item, i) => (
              <motion.div
                key={i}
                className="glass-panel p-5 flex items-start gap-4 hover:border-primary-500/20 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 + i * 0.08 }}
              >
                <div className="w-10 h-10 rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center text-primary-400 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-white text-sm mb-0.5">
                    {item.title}
                  </h4>
                  {item.action ? (
                    <a
                      href={item.action}
                      className="text-gray-400 text-sm hover:text-primary-400 transition-colors"
                    >
                      {item.info}
                    </a>
                  ) : (
                    <p className="text-gray-400 text-sm">{item.info}</p>
                  )}
                </div>
              </motion.div>
            ))}

            <div className="glass-panel p-6 mt-4">
              <h4 className="font-display font-bold text-white mb-2">
                Free Consultation
              </h4>
              <p className="text-sm text-gray-400 leading-relaxed mb-4">
                Every project starts with a free 30-minute discovery call. No
                commitment, no sales pressure — just an honest conversation about
                what you need and how we can help.
              </p>
              <button
                type="button"
                onClick={openScheduleCall}
                className="text-sm font-semibold text-primary-400 hover:text-primary-300 transition-colors"
              >
                Schedule on Zoom →
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
